import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Toolbar, AppBar, CssBaseline, IconButton, Box, Menu, MenuItem, CircularProgress
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import axios from 'axios';

import {
  PendingActions as PendingActionsIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Widgets as WidgetsIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  ArchiveOutlined as ArchiveOutlinedIcon,
  RecentActorsOutlined as RecentActorsOutlinedIcon
} from '@mui/icons-material';

import NotificationsIcon from '@mui/icons-material/Notifications';

import recruiterTheme, { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, ELLIPSIS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT } from '../../styles/recruiterTheme';
import JobDescriptionForm from './JobDescriptionForm.js';
import JDDetails from '../shared/JDDetails.js';
import CandidateProfiles from '../shared/CandidateProfile.js';
import ArchivedJdsPage from './ArchivedJdsPage.js';
import '../../styles/HiringdashBoard.css';
import ProfileForm from '../shared/Profileform.js';
import Pagination from '../../utils/Pagination.js';
import logo from '../../styles/logo.png';
import PendingData from '../shared/PendingData.js';
import NotificationDialog from '../shared/NotificationDialog';
import Reports from '../shared/Reports';
import QuestionDialog from '../shared/QuestionDialog.js';
import useActivityLogger from '../../hooks/useActivityLogger';
import ClientManagement from "./Clientmanagement.js";
import RbotManagerDashboard from "../ManagerPages/RbotManagerDashboard.js";
import CandidateBrowserPage from "../ManagerPages/CandidateManagement/Candidatebrowserpage.js";

// ─── API constants (module-level — read once, never recreated) ────────────────
const API = {
  fetchJds:             process.env.REACT_APP_FETCH_JDS_API,
  logout:               process.env.REACT_APP_LOGOUT_API,
  deleteJd:             process.env.REACT_APP_DELETE_JD_API,
  candidateProfileByJD: process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API,
  generatedQuestions:   process.env.REACT_APP_GENERATED_QUESTIONS_URL,
};

const PENDING_STATUSES = new Set(['Recruiter_Accepted', 'Interviewer_Selected']);
const ITEMS_PER_PAGE   = 10;

// ─── Global styles (fonts load from public/index.html) ────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
`;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatDot = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-2px); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Container = styled.div`
  display: flex;
  background: #F3EEF9;
  min-height: 100vh;
  font-family: ${FONT_BODY};
  padding-top: 72px;
  overflow: hidden;
`;

const CenteredSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
  height: calc(100vh - 72px);
  background: #F3EEF9;
`;

const ScrollableContent = styled.div`
  width: 100%;
  min-height: 100%;
  padding-bottom: 40px;
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  background: linear-gradient(170deg, #2D1B5E 0%, #4F378B 55%, #6750A4 100%);
  height: calc(100vh - 72px);
  padding: 24px 16px;
  position: sticky;
  top: 0;
  overflow-y: auto;
  box-shadow: 4px 0 24px rgba(79,55,139,0.18);
  z-index: 10;
`;

const SidebarLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY};
    font-size: ${TYPE.overline}px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1.25;
    text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin: 16px 0 8px 12px;
  }
`;

const NavButton = styled(IconButton)`
  && {
    width: 100%;
    border-radius: ${RADIUS.sm}px;
    padding: 10px 12px;
    justify-content: flex-start;
    text-align: left;
    color: ${p => (p.$active ? '#fff' : 'rgba(255,255,255,0.82)')};
    background: ${p => (p.$active ? 'rgba(255,255,255,0.18)' : 'transparent')};
    transition: background 0.18s ease, color 0.18s ease;
    margin-bottom: 4px;
    gap: 12px;
    font-family: ${FONT_BODY};
    font-weight: 600;
    font-size: ${TYPE.body}px;
    line-height: 1.25;
    position: relative;
    &:hover {
      background: rgba(255,255,255,0.14);
      color: #fff;
    }
    svg { font-size: 20px; flex-shrink: 0; }
  }
`;

const SidebarDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  margin: 12px 0;
`;

// ─── AppBar ───────────────────────────────────────────────────────────────────
const StyledAppBar = styled(AppBar)`
  && {
    background: rgba(255,255,255,0.97) !important;
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(103,80,164,0.12) !important;
    box-shadow: 0 2px 20px rgba(79,55,139,0.08) !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 1300 !important;
  }
`;

const BrandText = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 800 !important;
    font-size: ${TYPE.section}px !important;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
`;

const StyledAvatar = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  color: #fff;
  font-size: ${TYPE.body}px;
  font-weight: 700;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(79,55,139,0.35);
  flex-shrink: 0;
`;

const BellButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    transition: background 0.2s;
    position: relative;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 16px 4px 4px;
  background: linear-gradient(135deg, #F8F4FF, #EDE7F6);
  border-radius: ${RADIUS.pill}px;
  border: 1px solid rgba(103,80,164,0.15);
  min-width: 160px;
  white-space: nowrap;
`;

// ─── Page shell ("cards on canvas") ───────────────────────────────────────────
// Transparent container: the page sits on the lavender canvas, sections inside
// are their own white cards. Every tab renders inside it (embedded tab components
// add no outer padding of their own) so every title starts at the same x.
const StyledPaper = styled.div`
  padding: ${PAGE_PADDING}px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// White card around a table (or a form section).
const TableCard = styled.div`
  background: #fff;
  border: 1px solid #EDE7F6;
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

// Two-line clamp for long free text (role titles).
const CellClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  word-break: break-word;
  ${p => p.minWidth ? `min-width: ${p.minWidth}px;` : ''}
  ${p => p.maxWidth ? `max-width: ${p.maxWidth}px;` : ''}
`;

// ─── Page Title ───────────────────────────────────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin: 4px 0 24px !important;
  }
`;

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg, #6750A4, #4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(103,80,164,0.28)'};
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px ${p => p.shadow || 'rgba(103,80,164,0.36)'};
  }
  &::after {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const StatNumber = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.22;
  svg { font-size: 40px; }
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const StyledTable = styled(Table)`
  && { border-collapse: separate; border-spacing: 0; }
  && tbody tr:last-child td { border-bottom: none !important; }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:hover td { background: #FAF7FF; }
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: #3D2B6B !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 16px !important;
    white-space: nowrap;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover { transform: translateY(-1px); box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.sm}px;
    padding: 0 12px !important;
    white-space: nowrap;
    transition: background 0.18s !important;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

// ─── Status Chip ──────────────────────────────────────────────────────────────
const ActiveChip = styled.span`
  background: linear-gradient(135deg, #D4FAE0, #B5EAC3);
  color: #1B6B3A;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  height: 24px;
  padding: 0 10px;
  border-radius: ${RADIUS.pill}px;
  line-height: 1;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #2E9B54;
    ${css`animation: ${floatDot} 2s ease-in-out infinite;`}
    display: inline-block;
  }
`;

// ─── Notification Menu ────────────────────────────────────────────────────────
const NotifItem = styled(MenuItem)`
  && {
    padding: 12px 16px !important;
    border-bottom: 1px solid #FAF7FF !important;
    transition: background 0.15s !important;
    &:hover { background: #F8F4FF !important; }
  }
`;

const NotifDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF6B6B, #FF4444);
  flex-shrink: 0;
  margin-top: 6px;
  box-shadow: 0 2px 6px rgba(255,68,68,0.4);
`;

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionAccent = styled.div`
  width: 4px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
`;

const iconBtnSx = (color, hover) => ({
  color, borderRadius: `${RADIUS.sm}px`, width: CONTROL_HEIGHT.sm, height: CONTROL_HEIGHT.sm,
  '&:hover': { background: hover },
});

// ─── Tab persistence ─────────────────────────────────────────────────────────
// Tabs restored on refresh and used as Back/Close targets. Transient views
// (JD form, JD details, candidate profiles) are never persisted.
const PERSISTABLE_TABS = new Set([
  'hiringDashboard', 'archivedJds', 'pendingData', 'clients',
  'candidateProfiles', 'teamAnalytics', 'report', 'profile',
]);
const DEFAULT_TAB = 'hiringDashboard';
const TAB_KEY = 'activeTab';
const RETURN_TAB_KEY = 'returnTab';

const readSession = (key) => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};
const writeSession = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const removeSession = (key) => {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
};

const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('rbotUser'));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const LoadingBlock = ({ label }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} gap={1.5}>
    <CircularProgress sx={{ color: '#6750A4' }} />
    {label && (
      <Typography sx={{ fontFamily: FONT_BODY, color: '#8B7DB5', fontSize: TYPE.body, lineHeight: 1.5 }}>{label}</Typography>
    )}
  </Box>
);

const ErrorBlock = ({ message, onRetry }) => (
  <Box sx={{ textAlign: 'center', py: 5 }}>
    <Typography sx={{ color: '#E53935', fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, mb: onRetry ? 1.5 : 0 }}>
      {message}
    </Typography>
    {onRetry && <GhostButton onClick={onRetry}>Retry</GhostButton>}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
function HiringManagerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  // User fields come from navigation state (fresh login) or, after a refresh /
  // new tab / direct URL, from the profile Login.js persisted in localStorage.
  const currentUser = useMemo(
    () => (location.state?.email ? location.state : readStoredUser()) || {},
    [location.state]
  );
  const { email, name, phone, userType, _id } = currentUser;
  const userData = useMemo(
    () => (email && userType ? { name: name || '', phone, email, userType, _id } : null),
    [name, phone, email, userType, _id]
  );

  const initialJds = Array.isArray(location.state?.jds) ? location.state.jds : null;
  const [jds, setJds] = useState(() => initialJds || []);
  const [jdsLoading, setJdsLoading] = useState(false);
  const [jdsLoaded, setJdsLoaded] = useState(() => Boolean(initialJds));
  const [jdsError, setJdsError] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentJDId, setCurrentJDId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isCreateJDOpen, setIsCreateJDOpen] = useState(false);
  const [isEditJDOpen, setIsEditJDOpen] = useState(false);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const saved = readSession(TAB_KEY);
    return saved && PERSISTABLE_TABS.has(saved) ? saved : DEFAULT_TAB;
  });
  const [tabRefreshKey, setTabRefreshKey] = useState(0);
  const [showCandidateProfiles, setShowCandidateProfiles] = useState(false);
  const [selectedCandidateProfileId, setSelectedCandidateProfileId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [subjectiveQuestions, setSubjectiveQuestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [questionsJdId, setQuestionsJdId] = useState(null);
  const [questionsLoadingJdId, setQuestionsLoadingJdId] = useState(null);

  const [notifAnchor, setNotifAnchor] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [jdCandidatesMap, setJdCandidatesMap] = useState({});
  const [candidateBrowserCompany, setCandidateBrowserCompany] = useState(null);

  // Request sequence numbers: only the latest response may update state.
  const jdsReqRef = useRef(0);
  const pendingReqRef = useRef(0);

  const { log } = useActivityLogger(email, userType);
  // ── Derived / memoised values ───────────────────────────────────────────────
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(jds.length / ITEMS_PER_PAGE)),
    [jds.length]
  );

  // Sort once when jds changes, not on every render inside JSX
  const sortedJds = useMemo(
    () => [...jds].sort((a, b) => new Date(b.creation_time) - new Date(a.creation_time)),
    [jds]
  );

  const displayedJds = useMemo(
    () => sortedJds.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [sortedJds, currentPage]
  );

  const notifications = useMemo(
    () =>
      jds.flatMap(jd => {
        const pending = jdCandidatesMap[jd._id] || [];
        return pending.length > 0
          ? [{ jdID: jd.jdID, jobRole: jd.jobRole, count: pending.length, jd }]
          : [];
      }),
    [jds, jdCandidatesMap]
  );

  // Whether to show the main dashboard table
  const showDashboard = useMemo(
    () => activeTab === DEFAULT_TAB && !isCreateJDOpen && !isEditJDOpen && !isViewJdDetailsOpen && !showCandidateProfiles,
    [activeTab, isCreateJDOpen, isEditJDOpen, isViewJdDetailsOpen, showCandidateProfiles]
  );

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchJds = useCallback(async () => {
    if (!email) return;
    const reqId = ++jdsReqRef.current;
    setJdsLoading(true);
    setJdsError('');
    try {
      const response = await axios.get(`${API.fetchJds}=${encodeURIComponent(email)}`);
      if (reqId !== jdsReqRef.current) return;
      const list = response.data?.activeJds;
      setJds(Array.isArray(list) ? list : []);
    } catch (err) {
      if (reqId !== jdsReqRef.current) return;
      console.error('Failed to fetch job descriptions:', err.message);
      setJdsError(err.response?.data?.message || 'Failed to load job descriptions. Please try again.');
    } finally {
      if (reqId === jdsReqRef.current) {
        setJdsLoading(false);
        setJdsLoaded(true);
      }
    }
  }, [email]);

  const fetchPendingCount = useCallback(async (currentJds) => {
    const reqId = ++pendingReqRef.current;
    if (!currentJds || currentJds.length === 0) {
      setJdCandidatesMap({});
      setPendingCount(0);
      return;
    }
    try {
      const responses = await Promise.all(
        currentJds.map(jd =>
          axios.get(`${API.candidateProfileByJD}/${jd._id}`)
            .then(res => ({ jdId: jd._id, data: res.data }))
            .catch(() => ({ jdId: jd._id, data: [] }))
        )
      );
      if (reqId !== pendingReqRef.current) return;
      const newMap = {};
      let totalPending = 0;
      responses.forEach(({ jdId, data }) => {
        const pending = Array.isArray(data)
          ? data.filter(p => PENDING_STATUSES.has(p?.overallStatus))
          : [];
        newMap[jdId] = pending;
        totalPending += pending.length;
      });
      setJdCandidatesMap(newMap);
      setPendingCount(totalPending);
    } catch (err) {
      console.error('Failed to fetch pending count:', err.message);
    }
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Only redirect when there is no logged-in user at all.
  useEffect(() => {
    if (!email) navigate('/login', { replace: true });
  }, [email, navigate]);

  // Initial JD fetch (also refreshes JDs seeded from the login response)
  useEffect(() => {
    fetchJds();
  }, [fetchJds]);

  // Re-fetch pending count whenever the JD list changes (including to empty)
  useEffect(() => {
    if (jdsLoaded) fetchPendingCount(jds);
  }, [jds, jdsLoaded, fetchPendingCount]);

  // Keep the current page valid when the JD list shrinks (e.g. after delete)
  useEffect(() => {
    setCurrentPage(p => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  // Persist the current tab so F5 restores it (transient views are skipped).
  useEffect(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(TAB_KEY, activeTab);
  }, [activeTab]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const showSnackbar = useCallback((msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  // Closes every inline sub-view so exactly one view is visible.
  const closeSubViews = useCallback(() => {
    setIsCreateJDOpen(false);
    setIsEditJDOpen(false);
    setIsViewJdDetailsOpen(false);
    setShowCandidateProfiles(false);
  }, []);

  // Remember the tab a sub-view was opened from, so Back/Close returns there.
  const rememberReturnTab = useCallback(() => {
    if (PERSISTABLE_TABS.has(activeTab)) writeSession(RETURN_TAB_KEY, activeTab);
  }, [activeTab]);

  /**
   * Single in-app tab switch used by the sidebar, Pending Actions, Profile and
   * PendingData's Back. Resets every sub-view and refetches that tab's data.
   */
  const switchTab = useCallback((tab) => {
    closeSubViews();
    setSelectedJD(null);
    removeSession(RETURN_TAB_KEY);
    if (tab === DEFAULT_TAB) fetchJds();
    setActiveTab(tab);
    // Bumping the key remounts self-loading tab components so they refetch,
    // even when the same sidebar item is clicked again.
    setTabRefreshKey(k => k + 1);
  }, [closeSubViews, fetchJds]);

  // ── Action handlers ─────────────────────────────────────────────────────────
  const handlePrevPage = useCallback(
    () => setCurrentPage(p => Math.max(p - 1, 1)),
    []
  );

  const handleNextPage = useCallback(
    () => setCurrentPage(p => Math.min(p + 1, totalPages)),
    [totalPages]
  );

  const handleDelete = useCallback(async () => {
    const jdId = currentJDId;
    if (!jdId || deleting) return;
    const jd = jds.find(j => j._id === jdId);
    setDeleting(true);
    try {
      await axios.put(`${API.deleteJd}/${encodeURIComponent(jdId)}`);
      log('ARCHIVE_JD', {
        jdId,
        targetName: jd?.jobRole,
        metadata: { jdID: jd?.jdID },
      });
      setOpenDeleteDialog(false);
      showSnackbar('Job description deleted successfully');
      fetchJds();
    } catch (err) {
      console.error('Failed to archive job description:', err.message);
      setOpenDeleteDialog(false);
      showSnackbar(err.response?.data?.message || 'Failed to delete job description. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [currentJDId, deleting, jds, log, fetchJds, showSnackbar]);

  const handleViewJD = useCallback((jd) => {
    if (!jd) return;
    rememberReturnTab();
    closeSubViews();
    setSelectedJD({ ...jd, userRole: 'hiringManager', hiringManagerEmail: email });
    setIsViewJdDetailsOpen(true);
    setActiveTab('');
  }, [rememberReturnTab, closeSubViews, email]);

  const handleEditClick = useCallback((jd) => {
    if (!jd) return;
    rememberReturnTab();
    closeSubViews();
    setSelectedJD(jd);
    setIsEditJDOpen(true);
    setActiveTab('');
  }, [rememberReturnTab, closeSubViews]);

  const handleCreateJD = useCallback(() => {
    rememberReturnTab();
    closeSubViews();
    setSelectedJD(null);
    setIsCreateJDOpen(true);
    setActiveTab('');
  }, [rememberReturnTab, closeSubViews]);

  const handleOpenDeleteDialog = useCallback(
    (jdId) => { setCurrentJDId(jdId); setOpenDeleteDialog(true); },
    []
  );

  const handleCloseDeleteDialog = useCallback(() => {
    if (!deleting) setOpenDeleteDialog(false);
  }, [deleting]);

  // Back/Close from the JD form, JD details or candidate profiles: return to
  // the tab the sub-view was opened from and refresh the JD list (it may
  // have been created/edited).
  const handleClose = useCallback(() => {
    const returnTab = readSession(RETURN_TAB_KEY);
    removeSession(RETURN_TAB_KEY);
    setSelectedJD(null);
    closeSubViews();
    setActiveTab(returnTab && PERSISTABLE_TABS.has(returnTab) ? returnTab : DEFAULT_TAB);
    fetchJds();
  }, [closeSubViews, fetchJds]);

  const handleProfileTabClick = useCallback(() => switchTab('profile'), [switchTab]);

  const handleArchiveButtonClick = useCallback(() => switchTab('archivedJds'), [switchTab]);

  const handleLogout = useCallback(async () => {
    const em = email || localStorage.getItem('email');
    let tokens = {};
    try {
      tokens = JSON.parse(localStorage.getItem('userTokens')) || {};
    } catch {
      tokens = {};
    }
    const storedToken = localStorage.getItem('token');
    const token = (em && tokens[em]) || (storedToken && storedToken !== 'null' ? storedToken : null);

    try {
      if (token) {
        await axios.post(API.logout, { token, email: em }, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (err) {
      console.error('Logout failed:', err.response?.data?.message || err.message);
    } finally {
      if (em) delete tokens[em];
      localStorage.setItem('userTokens', JSON.stringify(tokens));
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      localStorage.removeItem('rbotUser');
      removeSession(TAB_KEY);
      removeSession(RETURN_TAB_KEY);
      navigate('/login', { replace: true });
    }
  }, [email, navigate]);

  const handleViewCandidateProfiles = useCallback((jdId) => {
    if (!jdId) return;
    rememberReturnTab();
    closeSubViews();
    setSelectedCandidateProfileId(jdId);
    setShowCandidateProfiles(true);
    setActiveTab('');
  }, [rememberReturnTab, closeSubViews]);

  const handlePendingCandidatesData = useCallback(() => switchTab('pendingData'), [switchTab]);

  const handleClientsTab = useCallback(() => switchTab('clients'), [switchTab]);

  const handleViewClientJobs = useCallback((client) => {
    setCandidateBrowserCompany(client?.company_name || null);
    switchTab('candidateProfiles');
  }, [switchTab]);

  const handleCandidateProfilesTab = useCallback(() => {
    setCandidateBrowserCompany(null);
    switchTab('candidateProfiles');
  }, [switchTab]);

  const fetchQuestions = useCallback(async (jdID) => {
    if (!jdID) return;
    setQuestionsLoadingJdId(jdID);
    try {
      const response = await axios.get(`${API.generatedQuestions}/${encodeURIComponent(jdID)}`);
      setMcqQuestions(response.data?.mcq_questions || []);
      setCodingQuestions(response.data?.coding_questions || []);
      setSubjectiveQuestions(response.data?.subjective_questions || []);
      setQuestionsJdId(jdID);
      setOpen(true);
    } catch (err) {
      console.error(`Failed to fetch questions for JD ${jdID}:`, err.message);
      showSnackbar(err.response?.data?.message || 'Failed to load questions for this JD. Please try again.');
    } finally {
      setQuestionsLoadingJdId(null);
    }
  }, [showSnackbar]);

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
      <GlobalStyle />
      <Container>
        <CssBaseline />

        {/* ── AppBar ── */}
        <StyledAppBar position="fixed">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 72, padding: '0 24px' }}>

            {/* Brand */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '24px', borderRight: '1.5px solid rgba(103,80,164,0.1)' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: `${RADIUS.md}px`, background: 'linear-gradient(135deg, #EDE7F6, #D0BCFF)', border: '2px solid rgba(103,80,164,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(79,55,139,0.18)', flexShrink: 0 }}>
                <img src={logo} alt="Logo" style={{ height: 36, width: 36, borderRadius: RADIUS.sm }} />
              </Box>
              <Box>
                <BrandText>Resourcing Bot</BrandText>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#8B7DB5', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.25, mt: '4px' }}>
                  Recruitment Platform
                </Typography>
              </Box>
            </Box>

            {/* Right side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <BellButton onClick={(e) => setNotifAnchor(e.currentTarget)}>
                <NotificationsIcon />
                {notifications.length > 0 && (
                  <Box sx={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: '8px', background: 'linear-gradient(135deg, #FF6B6B, #FF4444)', color: '#fff', fontSize: TYPE.overline - 1, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: '3px', boxShadow: '0 2px 6px rgba(255,68,68,0.5)', border: '1.5px solid #fff', lineHeight: 1, pointerEvents: 'none' }}>
                    {notifications.length > 99 ? '99+' : notifications.length}
                  </Box>
                )}
              </BellButton>

              <Menu
                anchorEl={notifAnchor}
                open={Boolean(notifAnchor)}
                onClose={() => setNotifAnchor(null)}
                PaperProps={{
                  sx: { width: 380, maxHeight: 420, borderRadius: `${RADIUS.lg}px`, boxShadow: '0 12px 40px rgba(79,55,139,0.18)', border: '1px solid #EDE7F6', mt: 1 }
                }}
              >
                {notifications.length === 0 ? (
                  <Box sx={{ p: '24px', textAlign: 'center', color: '#8B7DB5', fontFamily: FONT_BODY, fontSize: TYPE.body }}>
                    No new notifications
                  </Box>
                ) : (
                  notifications.map((n, i) => (
                    <NotifItem key={n.jd?._id || i} onClick={() => { handleViewCandidateProfiles(n.jd._id); setNotifAnchor(null); }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', width: '100%' }}>
                        <NotifDot />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: '#2D1B5E', lineHeight: 1.5, ...ELLIPSIS }} title={`${n.jdID} — ${n.jobRole}`}>
                            {n.jdID} — {n.jobRole}
                          </Typography>
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: '#8B7DB5', lineHeight: 1.5 }}>
                            {n.count} candidate(s) waiting for review
                          </Typography>
                        </Box>
                      </Box>
                    </NotifItem>
                  ))
                )}
              </Menu>

              {userData && (
                <UserCard>
                  <StyledAvatar>{(userData.name || userData.email || '?').charAt(0).toUpperCase()}</StyledAvatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, color: '#2D1B5E', lineHeight: 1.25 }}>
                      {userData.name}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: TYPE.caption, color: '#8B7DB5', lineHeight: 1.25, textTransform: 'capitalize' }}>
                      {userData.userType}
                    </Typography>
                  </Box>
                </UserCard>
              )}
            </Box>
          </Toolbar>
        </StyledAppBar>

        {/* ── Sidebar ── */}
        <MenuContainer>
          <SidebarLabel>Hiring Tools</SidebarLabel>

          <NavButton $active={activeTab === DEFAULT_TAB} onClick={() => switchTab(DEFAULT_TAB)}>
            <WidgetsIcon /><span>Hiring Dashboard</span>
          </NavButton>

          <NavButton $active={activeTab === 'archivedJds'} onClick={handleArchiveButtonClick}>
            <ArchiveOutlinedIcon /><span>Archived JDs</span>
          </NavButton>

          <NavButton $active={activeTab === 'pendingData'} onClick={handlePendingCandidatesData}>
            <PendingActionsIcon /><span>Pending Actions</span>
            {pendingCount > 0 && (
              <Box sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 12, minWidth: 20, height: 20, borderRadius: '10px', background: 'linear-gradient(135deg, #FF6B6B, #FF4444)', color: '#fff', fontSize: TYPE.overline, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: '4px', boxShadow: '0 2px 6px rgba(255,68,68,0.5)', lineHeight: 1 }}>
                {pendingCount > 99 ? '99+' : pendingCount}
              </Box>
            )}
          </NavButton>

          <NavButton $active={activeTab === 'clients'} onClick={handleClientsTab}>
            <BusinessIcon /><span>Clients</span>
          </NavButton>

          <NavButton $active={activeTab === 'candidateProfiles'} onClick={handleCandidateProfilesTab}>
            <RecentActorsOutlinedIcon /><span>Candidate Profiles</span>
          </NavButton>

          <SidebarDivider />
          <SidebarLabel>Reporting</SidebarLabel>

          <NavButton $active={activeTab === 'teamAnalytics'} onClick={() => switchTab('teamAnalytics')}>
            <BarChartIcon /><span>Team Analytics</span>
          </NavButton>

          <NavButton $active={activeTab === 'report'} onClick={() => switchTab('report')}>
            <BarChartIcon /><span>Reports</span>
          </NavButton>

          <SidebarDivider />
          <SidebarLabel>Account</SidebarLabel>

          <NavButton $active={activeTab === 'profile'} onClick={handleProfileTabClick}>
            <PersonIcon /><span>Profile</span>
          </NavButton>

          <NavButton onClick={handleLogout}>
            <ExitToAppIcon /><span>Logout</span>
          </NavButton>
        </MenuContainer>

        {/* ── Main Content ── */}
        <CenteredSection>
          <ScrollableContent>

            {/* ── Hiring Dashboard ── */}
            {showDashboard && (
              <StyledPaper>
                <Box sx={{ mb: 3 }}>
                  <PageTitle>Hiring Dashboard</PageTitle>
                  <PageSubtitle sx={{ mb: '0 !important' }}>Manage job descriptions and track your hiring pipeline</PageSubtitle>
                </Box>
                <StatGrid>
                  <StatCard gradient="linear-gradient(135deg, #6750A4, #4F378B)" shadow="rgba(103,80,164,0.32)">
                    <StatText><StatNumber>{jdsLoaded ? jds.length : '—'}</StatNumber><StatLabel>Active Jobs</StatLabel></StatText>
                    <StatIcon><WidgetsIcon /></StatIcon>
                  </StatCard>
                  <StatCard gradient="linear-gradient(135deg, #E91E8C, #C2185B)" shadow="rgba(233,30,140,0.28)">
                    <StatText><StatNumber>{jdsLoaded ? pendingCount : '—'}</StatNumber><StatLabel>Pending</StatLabel></StatText>
                    <StatIcon><PendingActionsIcon /></StatIcon>
                  </StatCard>
                </StatGrid>

                {!jdsLoaded || (jdsLoading && jds.length === 0) ? (
                  <LoadingBlock label="Loading job descriptions…" />
                ) : jdsError && jds.length === 0 ? (
                  <ErrorBlock message={jdsError} onRetry={fetchJds} />
                ) : jds.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography sx={{ fontFamily: FONT_BODY, color: '#B0A0D0', fontSize: TYPE.body, lineHeight: 1.5, mb: 2 }}>
                      No job descriptions found. Create your first one!
                    </Typography>
                    <PrimaryButton onClick={handleCreateJD}>+ Create New Job</PrimaryButton>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <SectionHeader style={{ marginBottom: 0 }}>
                        <SectionAccent />
                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: '#2D1B5E' }}>
                          Active Job Descriptions
                        </Typography>
                      </SectionHeader>
                      <PrimaryButton onClick={handleCreateJD}>
                        + Create New Job
                      </PrimaryButton>
                    </Box>

                    <TableCard><TableScroll>
                    <StyledTable>
                      <TableHead>
                        <TableRow>
                          <HeadCell>JD ID</HeadCell>
                          <HeadCell>Job Status</HeadCell>
                          <HeadCell>Job Role</HeadCell>
                          <HeadCell>Candidates</HeadCell>
                          <HeadCell>Questions</HeadCell>
                          <HeadCell align="right">Actions</HeadCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedJds.map((jd) => (
                          <BodyRow key={jd._id}>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{jd.jdID}</span>
                                <IconButton
                                  onClick={() => handleViewJD(jd)}
                                  title="View JD"
                                  sx={{ ...iconBtnSx('#6750A4', 'rgba(103,80,164,0.08)'), width: 28, height: 28 }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}><ActiveChip>Active</ActiveChip></BodyCell>
                            <BodyCell sx={{ fontWeight: '600 !important' }}>
                              <CellClamp minWidth={200} title={jd.jobRole || undefined}>{jd.jobRole}</CellClamp>
                            </BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                              <GhostButton onClick={() => handleViewCandidateProfiles(jd._id)}>
                                View Candidates
                              </GhostButton>
                            </BodyCell>
                            <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                              <GhostButton
                                onClick={() => fetchQuestions(jd.jdID)}
                                disabled={questionsLoadingJdId === jd.jdID}
                              >
                                {questionsLoadingJdId === jd.jdID
                                  ? <><CircularProgress size={14} sx={{ color: '#6750A4', mr: 1 }} />Loading…</>
                                  : 'View Questions'}
                              </GhostButton>
                            </BodyCell>
                            <BodyCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  onClick={() => handleEditClick(jd)}
                                  sx={iconBtnSx('#6750A4', 'rgba(103,80,164,0.08)')}
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleOpenDeleteDialog(jd._id)}
                                  sx={iconBtnSx('#C2185B', 'rgba(194,24,91,0.08)')}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            </BodyCell>
                          </BodyRow>
                        ))}
                      </TableBody>
                    </StyledTable>
                    </TableScroll></TableCard>

                    {totalPages > 1 && (
                      <Pagination
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePrevPage={handlePrevPage}
                        handleNextPage={handleNextPage}
                      />
                    )}
                  </>
                )}
              </StyledPaper>
            )}

            {/* ── Create / Edit JD Form ── */}
            {(isCreateJDOpen || isEditJDOpen) && (
              <StyledPaper>
                <JobDescriptionForm
                  jd={selectedJD}
                  isEdit={isEditJDOpen}
                  hiringManagerEmail={email}
                  onClose={handleClose}
                  onSnackbarOpen={() => {}}
                  fetchJds={isEditJDOpen ? fetchJds : undefined}
                />
              </StyledPaper>
            )}

            {/* ProfileForm pads itself (PAGE_PADDING), like on the recruiter dashboard. */}
            {activeTab === 'profile' && <ProfileForm key={tabRefreshKey} userData={userData} />}

            {activeTab === 'archivedJds' && (
              <StyledPaper><ArchivedJdsPage key={tabRefreshKey} hiringManagerEmail={email} onBack={() => switchTab(DEFAULT_TAB)} /></StyledPaper>
            )}

            {/* ── Clients ── */}
            {activeTab === 'clients' && (
              <StyledPaper>
                <ClientManagement
                  key={tabRefreshKey}
                  onViewJobs={handleViewClientJobs}
                  onViewCandidates={handleViewClientJobs}
                />
              </StyledPaper>
            )}

            {/* ── Candidate Profiles: full-height three-pane browser, rendered full-bleed ── */}
            {activeTab === 'candidateProfiles' && (
              <CandidateBrowserPage key={tabRefreshKey} initialCompany={candidateBrowserCompany} height="calc(100vh - 72px)" />
            )}

            {/* RbotManagerDashboard renders its own title in its hero banner — no second PageTitle here. */}
            {activeTab === 'teamAnalytics' && (
              <StyledPaper>
                <RbotManagerDashboard key={tabRefreshKey} />
              </StyledPaper>
            )}

            {activeTab === 'report' && (
              <StyledPaper>
                <PageTitle>Reports</PageTitle>
                <PageSubtitle>Analytics and insights for your hiring pipeline</PageSubtitle>
                <Reports key={tabRefreshKey} />
              </StyledPaper>
            )}

            {activeTab === 'pendingData' && (
              <StyledPaper>
                <PendingData
                  key={tabRefreshKey}
                  email={email}
                  userType={userType}
                  onBack={() => switchTab(DEFAULT_TAB)}
                />
              </StyledPaper>
            )}

            {isViewJdDetailsOpen && (
              <JDDetails
                jdDetails={selectedJD}
                userRole="hiringManager"
                hiringManagerEmail={email}
                onBack={handleClose}
              />
            )}

            {showCandidateProfiles && selectedCandidateProfileId && (
              <CandidateProfiles
                jdId={selectedCandidateProfileId}
                userType={userType}
                onBack={handleClose}
              />
            )}

            <NotificationDialog
              open={snackbarOpen}
              message={snackbarMessage}
              onClose={() => setSnackbarOpen(false)}
            />

            <QuestionDialog
              open={open}
              setOpen={setOpen}
              jdId={questionsJdId}
              mcqQuestions={mcqQuestions}
              codingQuestions={codingQuestions}
              subjectiveQuestions={subjectiveQuestions}
              onQuestionsUpdated={() => fetchQuestions(questionsJdId)}
              performedBy={email}
              role={userType}
            />

            {/* ── Delete Confirmation Dialog ── */}
            <Dialog
              open={openDeleteDialog}
              onClose={handleCloseDeleteDialog}
              PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px`, padding: '8px', boxShadow: '0 12px 40px rgba(79,55,139,0.2)' } }}
            >
              <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, color: '#2D1B5E', fontSize: TYPE.section }}>
                Delete Job Description
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontFamily: FONT_BODY, color: '#5C4A8A', fontSize: TYPE.body, lineHeight: 1.5 }}>
                  Are you sure you want to delete this job description? This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ padding: '16px 24px', gap: 1.5 }}>
                <GhostButton
                  onClick={handleCloseDeleteDialog}
                  disabled={deleting}
                  sx={{ height: `${CONTROL_HEIGHT.md}px`, border: '1px solid rgba(103,80,164,0.3) !important' }}
                >
                  Cancel
                </GhostButton>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                  sx={{
                    background: 'linear-gradient(135deg, #E53935, #C62828) !important',
                    color: '#fff !important', fontFamily: `${FONT_BODY} !important`,
                    fontWeight: '600 !important', fontSize: `${TYPE.small}px !important`,
                    borderRadius: `${RADIUS.sm}px !important`, height: CONTROL_HEIGHT.md, padding: '0 16px !important',
                    textTransform: 'none !important',
                    boxShadow: '0 4px 14px rgba(229,57,53,0.3) !important',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 7px 20px rgba(229,57,53,0.4) !important' },
                  }}
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>

          </ScrollableContent>
        </CenteredSection>
      </Container>
    </ThemeProvider>
  );
}

export default HiringManagerDashboard;
